import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { getAuthToken } from "../utils/auth";

type SortField = "name" | "type" | "email_count";
type SortDirection = "asc" | "desc";

interface SailthruList {
  name: string;
  type: "normal" | "smart";
  email_count?: number;
  create_time?: string;
}

interface ListsResponse {
  lists: SailthruList[];
  summary: {
    total: number;
    smart: number;
    normal: number;
  };
}

interface ListDetailResponse {
  list: SailthruList & {
    query?: Record<string, unknown>;
  };
}

const SailthruLists: React.FC = () => {
  const [listsData, setListsData] = useState<ListsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "smart" | "normal">("all");
  const [selectedList, setSelectedList] = useState<SailthruList | null>(null);
  const [listDetail, setListDetail] = useState<ListDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Export state
  const [, setExportJobId] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<"idle" | "pending" | "completed" | "failed">("idle");
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max polling time

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/sailthru-lists`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ListsResponse = await response.json();
      setListsData(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch lists");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchListDetail = useCallback(async (listName: string) => {
    setDetailLoading(true);

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(
        `${apiUrl}/prod/sailthru-lists/${encodeURIComponent(listName)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ListDetailResponse = await response.json();
      setListDetail(data);
    } catch (err: any) {
      console.error("Failed to fetch list detail:", err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const handleListClick = (list: SailthruList) => {
    setSelectedList(list);
    fetchListDetail(list.name);
  };

  const handleCloseDetail = () => {
    setSelectedList(null);
    setListDetail(null);
    // Reset export state
    setExportJobId(null);
    setExportStatus("idle");
    setExportUrl(null);
    setExportError(null);
    pollStartTimeRef.current = null;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startExport = async (listName: string) => {
    setExportStatus("pending");
    setExportError(null);
    setExportUrl(null);

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(
        `${apiUrl}/prod/sailthru-export/${encodeURIComponent(listName)}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setExportJobId(data.jobId);
      pollStartTimeRef.current = Date.now();

      // Start polling for job status
      pollIntervalRef.current = setInterval(() => {
        checkJobStatus(data.jobId);
      }, 2000);
    } catch (err: any) {
      setExportStatus("failed");
      setExportError(err.message || "Failed to start export");
    }
  };

  const checkJobStatus = async (jobId: string) => {
    // Check for timeout
    if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > POLL_TIMEOUT_MS) {
      setExportStatus("failed");
      setExportError("Export timed out after 5 minutes");
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      pollStartTimeRef.current = null;
      return;
    }

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/sailthru-export/job/${jobId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === "completed") {
        setExportStatus("completed");
        setExportUrl(data.exportUrl);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        pollStartTimeRef.current = null;
      } else if (data.status === "failed") {
        setExportStatus("failed");
        setExportError("Export job failed");
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        pollStartTimeRef.current = null;
      }
    } catch (err: any) {
      console.error("Failed to check job status:", err);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const getTypeChip = (type: string) => {
    return (
      <Chip
        label={type.toUpperCase()}
        color={type === "smart" ? "primary" : "default"}
        size="small"
        variant={type === "smart" ? "filled" : "outlined"}
      />
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredLists =
    listsData?.lists.filter((list) => {
      if (filter === "all") return true;
      return list.type === filter;
    }) || [];

  const sortedLists = [...filteredLists].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case "name":
        comparison = a.name.localeCompare(b.name);
        break;
      case "type":
        comparison = a.type.localeCompare(b.type);
        break;
      case "email_count":
        comparison = (a.email_count || 0) - (b.email_count || 0);
        break;
    }
    return sortDirection === "asc" ? comparison : -comparison;
  });

  const formatNumber = (num?: number) => {
    if (num === undefined) return "—";
    return num.toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Sailthru Smartlists</Typography>
        <Button
          variant="contained"
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
          }
          onClick={fetchLists}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "error.light" }}>
          <Typography color="error.contrastText">Error: {error}</Typography>
        </Paper>
      )}

      {listsData && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Lists
                </Typography>
                <Typography variant="h5">{listsData.summary.total}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Smart Lists
                </Typography>
                <Typography variant="h5" color="primary">
                  {listsData.summary.smart}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Normal Lists
                </Typography>
                <Typography variant="h5">{listsData.summary.normal}</Typography>
              </Box>
              <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                <Chip
                  icon={<FilterListIcon />}
                  label="All"
                  onClick={() => setFilter("all")}
                  color={filter === "all" ? "primary" : "default"}
                  variant={filter === "all" ? "filled" : "outlined"}
                />
                <Chip
                  label="Smart"
                  onClick={() => setFilter("smart")}
                  color={filter === "smart" ? "primary" : "default"}
                  variant={filter === "smart" ? "filled" : "outlined"}
                />
                <Chip
                  label="Normal"
                  onClick={() => setFilter("normal")}
                  color={filter === "normal" ? "primary" : "default"}
                  variant={filter === "normal" ? "filled" : "outlined"}
                />
              </Box>
            </Box>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "name"}
                      direction={sortField === "name" ? sortDirection : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      List Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "type"}
                      direction={sortField === "type" ? sortDirection : "asc"}
                      onClick={() => handleSort("type")}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === "email_count"}
                      direction={sortField === "email_count" ? sortDirection : "asc"}
                      onClick={() => handleSort("email_count")}
                    >
                      Email Count
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLists.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No lists found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedLists.map((list) => (
                    <TableRow
                      key={list.name}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleListClick(list)}
                    >
                      <TableCell>
                        <Typography fontWeight="medium">{list.name}</Typography>
                      </TableCell>
                      <TableCell>{getTypeChip(list.type)}</TableCell>
                      <TableCell align="right">{formatNumber(list.email_count)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!listsData && !loading && !error && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No list data available.</Typography>
        </Paper>
      )}

      <Dialog open={!!selectedList} onClose={handleCloseDetail} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{selectedList?.name}</Typography>
          <IconButton onClick={handleCloseDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedList ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Box sx={{ mt: 0.5 }}>{getTypeChip(selectedList.type)}</Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Email Count
                </Typography>
                <Typography variant="h6">
                  {formatNumber(selectedList.email_count)}
                </Typography>
              </Box>
              {listDetail?.list.query && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Query Definition
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      maxHeight: 300,
                      overflow: "auto",
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(listDetail.list.query, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {/* Export section */}
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Export List
                </Typography>
                {exportStatus === "idle" && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => startExport(selectedList.name)}
                  >
                    Export to CSV
                  </Button>
                )}
                {exportStatus === "pending" && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Preparing export...</Typography>
                    </Box>
                    <LinearProgress />
                  </Box>
                )}
                {exportStatus === "completed" && exportUrl && (
                  <Alert
                    severity="success"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        href={exportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </Button>
                    }
                  >
                    Export ready!
                  </Alert>
                )}
                {exportStatus === "failed" && (
                  <Alert severity="error">
                    {exportError || "Export failed"}
                  </Alert>
                )}
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">Failed to load list details.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SailthruLists;
